/**
 * Editing the pages a user category may open (Configuration > User categories).
 *
 * The administrator ticks pages in a list that mirrors the menu and sees each page in a small
 * preview. This module holds the pure parts: the draft selection, whether it differs from what is
 * saved, and how a page is previewed.
 */

/** A user category as the API returns it. */
export interface UserCategory {
    id: number;
    name: string;
    description: string;
    pageKeys: string[];
    memberCount: number;
}

/** What is sent to create or replace a category. */
export interface UserCategoryDraft {
    name: string;
    description: string;
    pageKeys: string[];
}

/** The prefix of the translatable keys the API answers a refused category change with. */
const CATEGORY_ERROR_KEY_PREFIX = 'configuration.permissions.errors.';

/** The `window.name` of the preview iframe. It survives a redirect inside the frame, a query does not. */
export const PREVIEW_FRAME_NAME = 'page-access-preview';

/** The size the page is laid out at inside the preview, before it is scaled down to fit. */
export const PREVIEW_FRAME_WIDTH = 1280;
export const PREVIEW_FRAME_HEIGHT = 800;

/** The smallest the preview is drawn, so a very narrow panel still shows something legible. */
const MIN_PREVIEW_SCALE = 0.2;

/** True when `window` is the preview iframe rather than the application itself. */
export function isPreviewFrame(windowLike: { name: string; self: unknown; top: unknown }): boolean {
    return windowLike.name === PREVIEW_FRAME_NAME && windowLike.self !== windowLike.top;
}

/**
 * How far to scale a page laid out `frameWidth` wide so it fits `containerWidth`. Never enlarges,
 * and never goes below a legible minimum. A container that has not been measured yet scales to the
 * minimum rather than to infinity.
 */
export function fitScale(containerWidth: number, frameWidth: number = PREVIEW_FRAME_WIDTH): number {
    if (Number.isNaN(containerWidth) || containerWidth <= 0) {
        return MIN_PREVIEW_SCALE;
    }

    return Math.min(1, Math.max(MIN_PREVIEW_SCALE, containerWidth / frameWidth));
}

/** The pages of `selected` after ticking or clearing `pageKey`, without duplicates. */
export function togglePageKey(
    selected: readonly string[],
    pageKey: string,
    checked: boolean,
): string[] {
    const others = selected.filter(key => key !== pageKey);

    return checked ? [...others, pageKey] : others;
}

/** True when two selections hold the same pages, whatever their order. */
export function sameKeySet(left: readonly string[], right: readonly string[]): boolean {
    const leftSet = new Set(left);

    return leftSet.size === new Set(right).size && right.every(key => leftSet.has(key));
}

/**
 * `selected` without the pages the menu no longer has. A category can still list a page that was
 * removed from the menu; it could never be unticked again, so saving drops it.
 */
export function retainKnownPages(
    selected: readonly string[],
    knownPageKeys: readonly string[],
): string[] {
    return selected.filter(key => knownPageKeys.includes(key));
}

/** The category's pages in menu order, for display and for a stable request. */
export function inMenuOrder(
    selected: readonly string[],
    knownPageKeys: readonly string[],
): string[] {
    return knownPageKeys.filter(key => selected.includes(key));
}

/**
 * The translation key to show for a failed category request. The API answers a refusal it expects
 * (name taken, category in use) with a key; anything else - a network failure, a server error - gets
 * `fallbackKey`.
 */
export function categoryErrorKey(error: unknown, fallbackKey: string): string {
    const message = error instanceof Error ? error.message : '';

    return message.startsWith(CATEGORY_ERROR_KEY_PREFIX) ? message : fallbackKey;
}
